import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { User } from '@prisma/client';
import { Spin } from 'antd';
import axios, { AxiosError } from 'axios';

import { Role } from '@app/domain-interfaces';

import { getAuthToken } from '../azure/msal/get-auth-token';

export type AuthUser = {
  id?: number;
  graphUserId?: string;
  enterpriseId?: string;
  name?: string;
  email?: string;
  badgeType?: string;
  roles: { [key: string]: boolean };
  testMode?: boolean;

  /**
   * Indicates if this user is a system admin.
   * Use this flag for system admin features that should be on regardless if roles are being spoofed.
   */
  isSystemAdmin?: boolean;
};

export type AuthState = {
  token?: string;
  expiry?: Date | null;
  user?: AuthUser;
  updateToken?: (token: string, expiry: Date | null) => void;
  error?: string;
};

export const UserAuthContext = React.createContext<AuthState | undefined>({});

interface ProviderProps {
  baseUrl: string;
  scopes: string[];
  children: ReactElement;
}
export function UserAuthProvider({ baseUrl, scopes, children }: ProviderProps) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress, accounts } = useMsal();
  const [authState, setAuthState] = useState<AuthState>();

  useEffect(() => {
    const account = instance.getActiveAccount();
    if ((!account || accounts.length === 0) && inProgress === InteractionStatus.None) {
      instance.loginRedirect({ scopes });
    } else if (accounts.length > 0) {
      // fetch token, expiration, populate AuthState with graph-user info and DB user-id
      const fetchToken = async () => {
        const { accessToken, expiry } = await getAuthToken(instance, scopes);

        instance.setActiveAccount(accounts[0]);

        const userRoles = Array.isArray(accounts[0]?.idTokenClaims?.roles) ? accounts[0].idTokenClaims.roles : [];
        const testMode = window.sessionStorage.getItem('saaf_test_roles_enabled') === 'true';
        const testRoles: Role[] = JSON.parse(window.sessionStorage.getItem('saaf_test_roles') ?? '[]');

        const isSystemAdmin = userRoles.includes(Role.SYSTEM_ADMIN);

        const roles: { [key: string]: boolean } = {
          [Role.SYSTEM_ADMIN]: testMode ? false : isSystemAdmin,
          [Role.BUNDLE_ADMIN]:
            (isSystemAdmin && (testMode ? testRoles.includes(Role.BUNDLE_ADMIN) : true)) ||
            userRoles.includes(Role.BUNDLE_ADMIN),
          [Role.RULE_ADMIN]:
            (isSystemAdmin && (testMode ? testRoles.includes(Role.RULE_ADMIN) : true)) ||
            userRoles.includes(Role.RULE_ADMIN),
          [Role.NODE_ACCESS_ADMIN]:
            (isSystemAdmin && (testMode ? testRoles.includes(Role.NODE_ACCESS_ADMIN) : true)) ||
            userRoles.includes(Role.NODE_ACCESS_ADMIN),
          [Role.NODE_ACCESS_EMS_ACCOUNT_MANAGER]:
            (isSystemAdmin && (testMode ? testRoles.includes(Role.NODE_ACCESS_EMS_ACCOUNT_MANAGER) : true)) ||
            userRoles.includes(Role.NODE_ACCESS_EMS_ACCOUNT_MANAGER),
          [Role.NODE_ACCESS_SUPPLIER_LEAD]:
            (isSystemAdmin && (testMode ? testRoles.includes(Role.NODE_ACCESS_SUPPLIER_LEAD) : true)) ||
            userRoles.includes(Role.NODE_ACCESS_SUPPLIER_LEAD),
          [Role.NODE_ACCESS_EXTERNAL_TSMC]:
            (isSystemAdmin && (testMode ? testRoles.includes(Role.NODE_ACCESS_EXTERNAL_TSMC) : true)) ||
            userRoles.includes(Role.NODE_ACCESS_EXTERNAL_TSMC),
        };

        const user: AuthUser = {
          graphUserId: accounts[0]?.localAccountId,
          enterpriseId: accounts[0]?.idTokenClaims?.enterpriseID as string,
          name: accounts[0]?.name,
          email: accounts[0]?.username,
          badgeType: accounts[0]?.idTokenClaims?.employeeBadgeType as string,
          isSystemAdmin,
          testMode,
          roles,
        };

        // Get user-id from postgres DB for the currently logged in user
        if (user?.enterpriseId && !authState?.user?.id) {
          try {
            const res = await axios.get<User>(`${baseUrl}/user/me`, {
              headers: { Authorization: 'Bearer ' + accessToken },
              // params: {
              //   enterpriseId: user.enterpriseId,
              // },
            });

            setAuthState({
              expiry: expiry,
              token: accessToken,
              user: { ...user, id: res.data.id },
            });
          } catch (error) {
            setAuthState({
              expiry: expiry,
              token: accessToken,
              user: { ...user, id: -1 },
              error: `Failed to get user id from SAAF DB. ${(error as AxiosError)?.message}`,
            });
          }
        }
      };

      fetchToken();
    }
  }, [accounts, authState, baseUrl, inProgress, instance, scopes]);

  return isAuthenticated && inProgress === InteractionStatus.None ? (
    <UserAuthContext.Provider
      value={{
        ...authState,
        updateToken: (token, expiry) => setAuthState({ ...authState, token, expiry }),
      }}
    >
      {children}
    </UserAuthContext.Provider>
  ) : (
    <Spin size="large" style={{ minHeight: '100vh' }}>
      <div style={{ height: '100vh' }} />
    </Spin>
  );
}

export const useUserAuth = () => {
  const context = useContext(UserAuthContext);

  if (!context) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }

  return context;
};
